// EN/FR copy for Additional Contributions (form, detail and approval timeline). These views render
// their own language (data-cvi-i18n="managed"), so text people typed is never machine-translated.

const plural = (n, one, many) => (n === 1 ? one : many);

const COPY = {
    en: {
        locale: "en-CA",
        common: {
            back: "Back to Additional Contributions",
            cancel: "Cancel",
            optional: "Optional",
            required: "Required",
            notRecorded: "Not recorded",
            you: "you",
        },
        status: {
            draft: "Draft", pending_review: "Pending review", resubmitted: "Resubmitted", revision_requested: "Revision requested",
            approved: "Approved", rejected: "Rejected",
        },
        statusHelp: {
            draft: "Not submitted yet. Your manager reviews it only after you submit it.",
            pending_review: "Submitted and waiting for your manager’s review.",
            resubmitted: "Revised and sent back to your manager for review.",
            revision_requested: "Your manager asked for changes. Update it and resubmit.",
            approved: "Approved. It counts toward your Individual Contribution.",
            rejected: "Not accepted. It does not count toward your Individual Contribution.",
        },
        form: {
            title: { create: "Log a contribution", draft: "Edit draft", revise: "Revise and resubmit" },
            subtitle: {
                create: "Record an achievement, project or initiative beyond your core responsibilities. Your manager reviews it and sets its impact level.",
                draft: "Finish your draft, then submit it to your manager for review.",
                revise: "Address your manager’s feedback, then resubmit the contribution for review.",
            },
            period: "Period",
            sections: {
                classification: "Classification",
                classificationHelp: "Choose where this contribution fits. Categories are set by your organization.",
                details: "What you contributed",
                detailsHelp: "Your manager uses this description to decide the impact level.",
                evidence: "Evidence",
                evidenceHelp: "Attach a document that supports the contribution, such as a report, certificate or screenshot.",
                challenges: "Challenges and support needs",
                challengesHelp: "If something is blocking this work, note it so your manager can follow up. It does not affect the score.",
            },
            category: "Category", categoryPlaceholder: "Select a category",
            subcategory: "Subcategory", subcategoryPlaceholder: "Select a subcategory", subcategoryFirst: "Select a category first",
            lockedClassification: "The category cannot be changed while the contribution is under revision.",
            titleLabel: "Title", titlePlaceholder: "Example: Automated the release-readiness checklist",
            description: "Description", descriptionPlaceholder: "What did you do, and what difference did it make for your team, clients or the organization?",
            counter: (n, max) => `${n} / ${max}`,
            impactNote: "Your manager sets the impact level when approving. Describe the outcome and who benefited.",
            feedbackTitle: "Feedback to address",
            upload: { cta: "Choose a file", or: "or drag it here", formats: "PDF, Word, JPG or PNG · up to 5 MB", replace: "Replace", remove: "Remove", selected: "New file, uploaded when you save", reading: "Reading file…" },
            existingFiles: "Files already attached",
            challenge: {
                type: "Type of challenge", none: "No challenge",
                types: {
                    EXTERNAL_DEPENDENCY: "External or cross-unit dependency", RESOURCE_LIMITATION: "Resource constraint", PROCESS_ISSUE: "Process issue",
                    SYSTEM_TOOL: "System or tool", MANAGEMENT_DECISION: "Management decision", OTHER: "Other",
                },
                status: "Status", statuses: { none: "No challenge", open: "Unresolved", in_progress: "In progress", resolved: "Resolved" },
                description: "Challenge description", support: "Support needed", action: "Suggested action",
            },
            errors: {
                category: "Select a category.",
                subcategory: "Select a subcategory.",
                title: "Enter a title.",
                description: min => `Describe the contribution in at least ${min} characters.`,
                fileType: "This file type is not accepted. Choose a PDF, Word, JPG or PNG file.",
                fileSize: "The file must be 5 MB or smaller.",
                fileRead: "The file could not be read. Try again.",
                summary: n => `${n} ${plural(n, "field needs", "fields need")} your attention before you can submit.`,
                noPeriod: "There is no active period. Contributions can only be recorded during an active period.",
                savedAsDraft: "Your contribution was saved as a draft, but it could not be submitted. Open it from the list to try again.",
                savedNotResubmitted: "Your changes were saved, but the contribution could not be resubmitted. Try again.",
                generic: "The contribution could not be saved. Try again.",
            },
            localDraft: { saved: time => `Unsaved changes kept on this device · ${time}`, idle: "Unsaved changes are kept on this device until you save.", clear: "Clear form" },
            actions: {
                saveDraft: "Save draft", savingDraft: "Saving…",
                submit: "Submit for review", submitting: "Submitting…",
                resubmit: "Resubmit for review", resubmitting: "Resubmitting…",
                draftHint: "A draft is not sent for review until you submit it.",
                submitHint: name => name ? `It will be sent to ${name} for review.` : "It will be sent to your manager for review.",
            },
            saved: { draft: "Draft saved.", submitted: "Contribution submitted for review.", resubmitted: "Contribution resubmitted for review." },
        },
        detail: {
            title: "Contribution details",
            category: "Category",
            owner: "Employee", submittedOn: "Submitted", notSubmitted: "Not submitted", reviewer: "Reviewer", decidedOn: "Latest decision", awaiting: "Awaiting decision",
            impact: "Impact level", score: "Points", period: "Period",
            description: "Description", noDescription: "No description was provided.",
            evidence: "Evidence", noEvidence: "No supporting file was attached.",
            challenge: "Challenge and support need",
            feedback: { title: "Latest reviewer feedback", none: "No reviewer feedback has been recorded yet.", revision: "Changes requested", approved: "Approval note", rejected: "Reason for rejection" },
            actions: { edit: "Edit draft", submit: "Submit for review", submitting: "Submitting…", revise: "Revise and resubmit" },
            downloadFailed: "The file could not be downloaded.",
        },
        timeline: {
            title: "Approval history",
            subtitle: "Every recorded step, oldest first.",
            current: "Current status",
            events: {
                created: "Created as a draft", submitted: "Submitted for review", resubmitted: "Resubmitted after revision",
                updated: "Details updated", revision_requested: "Revision requested", approved: "Approved", rejected: "Rejected",
                attachment_added: "File attached", attachment_removed: "File removed", other: "Updated",
            },
            by: name => `by ${name}`,
            byUnknown: "Actor not recorded",
            pending: {
                submit: "Submission",
                submitHelp: "Not submitted yet.",
                review: "Manager review",
                reviewHelp: name => name ? `Waiting for ${name}.` : "Waiting for the direct manager.",
                revise: "Revision by the employee",
                reviseHelp: name => name ? `Waiting for ${name} to revise and resubmit.` : "Waiting for the employee to revise and resubmit.",
            },
            outcome: { approved: "Final decision · approved", rejected: "Final decision · not accepted" },
            impactAwarded: (level, points) => `Impact level ${level}${points != null ? ` · ${points} ${plural(Number(points), "point", "points")}` : ""}`,
            minor: n => `${n} ${plural(n, "minor update", "minor updates")}`,
            limitation: {
                derived: "A detailed history was not recorded for this contribution. The dates shown come from the contribution record; who performed each step is not available.",
                actor: "Some steps were recorded without the person who performed them.",
            },
            empty: "No workflow step has been recorded yet.",
        },
        list: {
            continueDraft: "Draft · not submitted",
            updated: d => `Updated ${d}`,
            submitted: d => `Submitted ${d}`,
        },
    },
    fr: {
        locale: "fr-CA",
        common: {
            back: "Retour aux contributions additionnelles",
            cancel: "Annuler",
            optional: "Facultatif",
            required: "Obligatoire",
            notRecorded: "Non consigné",
            you: "vous",
        },
        status: {
            draft: "Brouillon", pending_review: "En attente d’examen", resubmitted: "Soumis de nouveau", revision_requested: "Révision demandée",
            approved: "Approuvé", rejected: "Rejeté",
        },
        statusHelp: {
            draft: "Pas encore soumis. Votre gestionnaire l’examinera seulement après la soumission.",
            pending_review: "Soumis et en attente de l’examen de votre gestionnaire.",
            resubmitted: "Révisé et renvoyé à votre gestionnaire pour examen.",
            revision_requested: "Votre gestionnaire a demandé des modifications. Mettez la contribution à jour et soumettez-la de nouveau.",
            approved: "Approuvé. La contribution compte dans votre contribution individuelle.",
            rejected: "Non retenu. La contribution ne compte pas dans votre contribution individuelle.",
        },
        form: {
            title: { create: "Ajouter une contribution", draft: "Modifier le brouillon", revise: "Réviser et soumettre de nouveau" },
            subtitle: {
                create: "Consignez une réalisation, un projet ou une initiative qui dépasse vos responsabilités principales. Votre gestionnaire l’examine et en fixe le niveau d’impact.",
                draft: "Terminez votre brouillon, puis soumettez-le à votre gestionnaire pour examen.",
                revise: "Tenez compte de la rétroaction de votre gestionnaire, puis soumettez de nouveau la contribution pour examen.",
            },
            period: "Période",
            sections: {
                classification: "Classement",
                classificationHelp: "Choisissez où se situe cette contribution. Les catégories sont définies par votre organisation.",
                details: "Votre contribution",
                detailsHelp: "Votre gestionnaire s’appuie sur cette description pour fixer le niveau d’impact.",
                evidence: "Pièce justificative",
                evidenceHelp: "Joignez un document qui appuie la contribution, comme un rapport, une attestation ou une capture d’écran.",
                challenges: "Défis et besoins de soutien",
                challengesHelp: "Si un obstacle freine ce travail, notez-le pour que votre gestionnaire puisse faire un suivi. Cela n’influe pas sur la note.",
            },
            category: "Catégorie", categoryPlaceholder: "Sélectionnez une catégorie",
            subcategory: "Sous-catégorie", subcategoryPlaceholder: "Sélectionnez une sous-catégorie", subcategoryFirst: "Sélectionnez d’abord une catégorie",
            lockedClassification: "La catégorie ne peut pas être modifiée pendant la révision de la contribution.",
            titleLabel: "Titre", titlePlaceholder: "Exemple : Automatisation de la liste de vérification avant mise en production",
            description: "Description", descriptionPlaceholder: "Qu’avez-vous fait et quelle différence cela a-t-il fait pour votre équipe, la clientèle ou l’organisation?",
            counter: (n, max) => `${n} / ${max}`,
            impactNote: "Votre gestionnaire fixe le niveau d’impact au moment de l’approbation. Décrivez le résultat et qui en a bénéficié.",
            feedbackTitle: "Rétroaction à prendre en compte",
            upload: { cta: "Choisir un fichier", or: "ou glissez-le ici", formats: "PDF, Word, JPG ou PNG · 5 Mo maximum", replace: "Remplacer", remove: "Retirer", selected: "Nouveau fichier, téléversé à l’enregistrement", reading: "Lecture du fichier…" },
            existingFiles: "Fichiers déjà joints",
            challenge: {
                type: "Type de défi", none: "Aucun défi",
                types: {
                    EXTERNAL_DEPENDENCY: "Dépendance externe ou entre unités", RESOURCE_LIMITATION: "Contrainte de ressources", PROCESS_ISSUE: "Problème de processus",
                    SYSTEM_TOOL: "Système ou outil", MANAGEMENT_DECISION: "Décision de la direction", OTHER: "Autre",
                },
                status: "Statut", statuses: { none: "Aucun défi", open: "Non résolu", in_progress: "En cours", resolved: "Résolu" },
                description: "Description du défi", support: "Soutien requis", action: "Action suggérée",
            },
            errors: {
                category: "Sélectionnez une catégorie.",
                subcategory: "Sélectionnez une sous-catégorie.",
                title: "Inscrivez un titre.",
                description: min => `Décrivez la contribution en au moins ${min} caractères.`,
                fileType: "Ce type de fichier n’est pas accepté. Choisissez un fichier PDF, Word, JPG ou PNG.",
                fileSize: "Le fichier doit faire 5 Mo ou moins.",
                fileRead: "Le fichier n’a pas pu être lu. Réessayez.",
                summary: n => `${n} ${plural(n, "champ demande", "champs demandent")} votre attention avant la soumission.`,
                noPeriod: "Aucune période n’est active. Les contributions ne peuvent être consignées que pendant une période active.",
                savedAsDraft: "Votre contribution a été enregistrée comme brouillon, mais elle n’a pas pu être soumise. Ouvrez-la à partir de la liste pour réessayer.",
                savedNotResubmitted: "Vos modifications ont été enregistrées, mais la contribution n’a pas pu être soumise de nouveau. Réessayez.",
                generic: "La contribution n’a pas pu être enregistrée. Réessayez.",
            },
            localDraft: { saved: time => `Modifications non enregistrées conservées sur cet appareil · ${time}`, idle: "Les modifications non enregistrées sont conservées sur cet appareil jusqu’à l’enregistrement.", clear: "Effacer le formulaire" },
            actions: {
                saveDraft: "Enregistrer le brouillon", savingDraft: "Enregistrement…",
                submit: "Soumettre pour examen", submitting: "Soumission…",
                resubmit: "Soumettre de nouveau", resubmitting: "Soumission…",
                draftHint: "Un brouillon n’est pas envoyé pour examen tant que vous ne le soumettez pas.",
                submitHint: name => name ? `Elle sera envoyée à ${name} pour examen.` : "Elle sera envoyée à votre gestionnaire pour examen.",
            },
            saved: { draft: "Brouillon enregistré.", submitted: "Contribution soumise pour examen.", resubmitted: "Contribution soumise de nouveau pour examen." },
        },
        detail: {
            title: "Détails de la contribution",
            category: "Catégorie",
            owner: "Personne employée", submittedOn: "Soumis le", notSubmitted: "Non soumis", reviewer: "Examinateur", decidedOn: "Dernière décision", awaiting: "En attente de décision",
            impact: "Niveau d’impact", score: "Points", period: "Période",
            description: "Description", noDescription: "Aucune description n’a été fournie.",
            evidence: "Pièce justificative", noEvidence: "Aucun fichier justificatif n’a été joint.",
            challenge: "Défi et besoin de soutien",
            feedback: { title: "Dernière rétroaction de l’examinateur", none: "Aucune rétroaction n’a encore été consignée.", revision: "Modifications demandées", approved: "Note d’approbation", rejected: "Motif du rejet" },
            actions: { edit: "Modifier le brouillon", submit: "Soumettre pour examen", submitting: "Soumission…", revise: "Réviser et soumettre de nouveau" },
            downloadFailed: "Le fichier n’a pas pu être téléchargé.",
        },
        timeline: {
            title: "Historique d’approbation",
            subtitle: "Chaque étape consignée, de la plus ancienne à la plus récente.",
            current: "Statut actuel",
            events: {
                created: "Créé comme brouillon", submitted: "Soumis pour examen", resubmitted: "Soumis de nouveau après révision",
                updated: "Renseignements mis à jour", revision_requested: "Révision demandée", approved: "Approuvé", rejected: "Rejeté",
                attachment_added: "Fichier joint", attachment_removed: "Fichier retiré", other: "Mis à jour",
            },
            by: name => `par ${name}`,
            byUnknown: "Auteur non consigné",
            pending: {
                submit: "Soumission",
                submitHelp: "Pas encore soumis.",
                review: "Examen du gestionnaire",
                reviewHelp: name => name ? `En attente de ${name}.` : "En attente du gestionnaire immédiat.",
                revise: "Révision par la personne",
                reviseHelp: name => name ? `En attente de la révision de ${name}.` : "En attente de la révision par la personne.",
            },
            outcome: { approved: "Décision finale · approuvé", rejected: "Décision finale · non retenu" },
            impactAwarded: (level, points) => `Niveau d’impact ${level}${points != null ? ` · ${points} ${plural(Number(points), "point", "points")}` : ""}`,
            minor: n => `${n} ${plural(n, "mise à jour mineure", "mises à jour mineures")}`,
            limitation: {
                derived: "Aucun historique détaillé n’a été consigné pour cette contribution. Les dates proviennent de la fiche de la contribution; la personne ayant effectué chaque étape n’est pas connue.",
                actor: "Certaines étapes ont été consignées sans la personne qui les a effectuées.",
            },
            empty: "Aucune étape n’a encore été consignée.",
        },
        list: {
            continueDraft: "Brouillon · non soumis",
            updated: d => `Mis à jour le ${d}`,
            submitted: d => `Soumis le ${d}`,
        },
    },
};

export function contributionCopy(language) {
    return COPY[language === "fr" ? "fr" : "en"];
}
